/**
 * Pure helpers for the Règles de gestion page (spec 2026-10-27-18, part A):
 * grouping rule families into one block, the scope a rule watches, and the
 * numeric fields of a condition a manager may edit.
 */

/** The minimal rule shape these helpers read. */
export interface FamilyRule {
  uid: string
  code?: string
  family?: string | null
  asset_type_uid?: string
  jurisdiction?: string
  check?: Record<string, unknown>
}

/** The minimal asset type shape these helpers read. */
export interface FamilyAssetType {
  uid: string
  code: string
  inherits_from_asset_type_uid?: string | null
}

export type RuleBlock<R extends FamilyRule> =
  | { kind: 'rule'; rule: R }
  | { kind: 'family'; family: string; members: R[] }

/** Depth of a type in the inheritance tree (root = 1); 0 for an unknown uid. */
export function typeDepth(types: FamilyAssetType[], uid: string | undefined): number {
  if (!uid) return 0
  const byUid = new Map(types.map((t) => [t.uid, t]))
  let depth = 0
  const seen = new Set<string>()
  let cur = byUid.get(uid)
  while (cur && !seen.has(cur.uid)) {
    seen.add(cur.uid)
    depth++
    cur = cur.inherits_from_asset_type_uid ? byUid.get(cur.inherits_from_asset_type_uid) : undefined
  }
  return depth
}

/**
 * Orders a family's members the way the server picks its default: untyped
 * first, then the root-most type, ties by code. The first member is the
 * default, the rest are its per-type overrides.
 */
export function sortFamilyMembers<R extends FamilyRule>(members: R[], types: FamilyAssetType[]): R[] {
  const rank = (r: R) => (r.asset_type_uid ? typeDepth(types, r.asset_type_uid) : -1)
  return [...members].sort((a, b) => rank(a) - rank(b) || (a.code ?? '').localeCompare(b.code ?? ''))
}

/**
 * Groups rules into blocks, keeping the incoming order: a rule outside any
 * family is its own block, and every member of a family lands in one block,
 * placed where its first member appeared.
 */
export function groupRuleBlocks<R extends FamilyRule>(rules: R[], types: FamilyAssetType[] = []): RuleBlock<R>[] {
  const blocks: RuleBlock<R>[] = []
  const byFamily = new Map<string, { kind: 'family'; family: string; members: R[] }>()
  for (const r of rules) {
    if (!r.family) {
      blocks.push({ kind: 'rule', rule: r })
      continue
    }
    let block = byFamily.get(r.family)
    if (!block) {
      block = { kind: 'family', family: r.family, members: [] }
      byFamily.set(r.family, block)
      blocks.push(block)
    }
    block.members.push(r)
  }
  for (const b of byFamily.values()) b.members = sortFamilyMembers(b.members, types)
  return blocks
}

/** Which assets a rule watches: every asset, every equipment, or one type. */
export type RuleScope = { kind: 'all_assets' } | { kind: 'all_equipment' } | { kind: 'type'; type?: FamilyAssetType }

export function ruleScope(rule: FamilyRule, types: FamilyAssetType[]): RuleScope {
  if (!rule.asset_type_uid) return { kind: 'all_assets' }
  const type = types.find((t) => t.uid === rule.asset_type_uid)
  if (type?.code === 'equipment') return { kind: 'all_equipment' }
  return { kind: 'type', type }
}

/** One editable number of a rule's condition. */
export interface NumericField {
  key: string
  /** `check` for a top-level check field, `params` for check.params. */
  scope: 'check' | 'params'
  value: number
}

/** Top-level check fields a manager edits (the rest are structural). */
const CHECK_NUMERIC_KEYS = ['value', 'window_months']

/**
 * Every editable number of a rule's condition: the check's value and window,
 * then each numeric parameter (min_baseline, grace_months…), in that order.
 */
export function numericFields(check: Record<string, unknown> | undefined): NumericField[] {
  if (!check) return []
  const out: NumericField[] = []
  for (const key of CHECK_NUMERIC_KEYS) {
    const v = check[key]
    if (typeof v === 'number') out.push({ key, scope: 'check', value: v })
  }
  const params = check.params
  if (params && typeof params === 'object' && !Array.isArray(params)) {
    for (const [key, v] of Object.entries(params as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))) {
      if (typeof v === 'number') out.push({ key, scope: 'params', value: v })
    }
  }
  return out
}

/** Writes edited numbers back into a copy of the check. */
export function applyNumericFields(
  check: Record<string, unknown>,
  edits: Record<string, number>,
  fields: NumericField[],
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...check }
  const params: Record<string, unknown> = { ...((check.params as Record<string, unknown>) ?? {}) }
  let paramsTouched = false
  for (const f of fields) {
    const v = edits[`${f.scope}.${f.key}`]
    if (v === undefined || !Number.isFinite(v)) continue
    if (f.scope === 'check') next[f.key] = v
    else {
      params[f.key] = v
      paramsTouched = true
    }
  }
  if (paramsTouched) next.params = params
  return next
}

/**
 * Pages through a list endpoint until `total` is reached: the server caps a
 * page at 100, and an organization holds more rules than that.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number, limit: number) => Promise<{ data: T[]; total: number }>,
  limit = 100,
): Promise<T[]> {
  const out: T[] = []
  for (let page = 1; page <= 100; page++) {
    const res = await fetchPage(page, limit)
    out.push(...res.data)
    if (res.data.length === 0 || out.length >= res.total) break
  }
  return out
}
